import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  confirmResetPassword as amplifyConfirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
  resetPassword as amplifyResetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  type AuthUser,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

/**
 * Staff session state for the Operations Console. Sign-in / sign-out /
 * session-restore only — there is **no sign-up or self-service confirm**:
 * staff accounts are admin-created in Cognito (design doc §3). First-time
 * users get an invite email with a temp password; the NEW_PASSWORD_REQUIRED
 * step is surfaced to the sign-in form so they can set a permanent one.
 *
 * Password *recovery* is self-service even though account *creation* is not.
 * The pool is `AccountRecovery.EMAIL_ONLY` (identity-stack.ts), so Cognito
 * mails a code to the address on the account and nowhere else — an admin
 * cannot point recovery at an address of their choosing, which is exactly the
 * property that makes it safe to expose on a public sign-in page.
 */

export const USER_ALREADY_AUTHENTICATED = "UserAlreadyAuthenticatedException";
const COGNITO_STORAGE_PREFIX = "CognitoIdentityServiceProvider.";

export interface SignInResult {
  needsNewPassword: boolean;
  /**
   * The account is in `RESET_REQUIRED` — someone ran `AdminResetUserPassword`
   * against it. Cognito has already mailed a code, so the form sends them
   * straight to the confirm step rather than making them ask for a second one.
   */
  needsPasswordReset: boolean;
}

export interface PasswordResetRequest {
  /** Masked destination Cognito reports, e.g. `c***@y***.com`, when it gives one. */
  destination: string | null;
}

export interface AuthState {
  status: "loading" | "signed-out" | "signed-in";
  user: AuthUser | null;
  signIn: (input: {
    email: string;
    password: string;
    newPassword?: string;
  }) => Promise<SignInResult>;
  requestPasswordReset: (email: string) => Promise<PasswordResetRequest>;
  confirmPasswordReset: (input: {
    email: string;
    code: string;
    newPassword: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  const refresh = useCallback(async () => {
    try {
      const current = await getCurrentUser();
      setUser(current);
      setStatus("signed-in");
    } catch {
      setUser(null);
      setStatus("signed-out");
    }
  }, []);

  useEffect(() => {
    void refresh();
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      if (
        payload.event === "signedIn" ||
        payload.event === "signedOut" ||
        payload.event === "tokenRefresh" ||
        payload.event === "tokenRefresh_failure"
      ) {
        void refresh();
      }
    });
    function onStorage(e: StorageEvent) {
      if (e.key === null || e.key.startsWith(COGNITO_STORAGE_PREFIX)) void refresh();
    }
    window.addEventListener("storage", onStorage);
    return () => {
      unsubscribe();
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      async signIn({ email, password, newPassword }) {
        let result;
        try {
          result = await amplifySignIn({ username: email, password });
        } catch (err) {
          // Amplify normally folds this state into the RESET_PASSWORD step
          // below, but it still surfaces as a raw throw on some paths. Either
          // way the caller gets one answer instead of an opaque error.
          if (errorName(err) === "PasswordResetRequiredException") {
            return { needsNewPassword: false, needsPasswordReset: true };
          }
          throw err;
        }
        if (result.isSignedIn) {
          await refresh();
          return { needsNewPassword: false, needsPasswordReset: false };
        }
        const step = result.nextStep.signInStep;
        if (step === "RESET_PASSWORD") {
          return { needsNewPassword: false, needsPasswordReset: true };
        }
        if (step === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          if (!newPassword) return { needsNewPassword: true, needsPasswordReset: false };
          const { confirmSignIn } = await import("aws-amplify/auth");
          const done = await confirmSignIn({ challengeResponse: newPassword });
          if (done.isSignedIn) {
            await refresh();
            return { needsNewPassword: false, needsPasswordReset: false };
          }
        }
        throw new Error(`Unexpected sign-in step: ${step}`);
      },
      async requestPasswordReset(email) {
        const out = await amplifyResetPassword({ username: email });
        const next = out.nextStep;
        if (next.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
          return { destination: next.codeDeliveryDetails?.destination ?? null };
        }
        // "DONE" means Cognito considers the reset already complete — nothing
        // was mailed, so there is no destination to name.
        return { destination: null };
      },
      async confirmPasswordReset({ email, code, newPassword }) {
        await amplifyConfirmResetPassword({
          username: email,
          confirmationCode: code,
          newPassword,
        });
        // Deliberately does *not* sign them in: Cognito issues no tokens here,
        // and the form sends them back to sign in with the password they just
        // chose — which also proves to them that it took.
      },
      async signOut() {
        await amplifySignOut();
        setUser(null);
        setStatus("signed-out");
      },
    }),
    [status, user, refresh],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

function errorName(err: unknown): string | null {
  return err && typeof err === "object" && "name" in err ? String(err.name) : null;
}

export function useAuth(): AuthState {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Current Cognito ID token (or null). The ops API authorizer's audience is
 * the SPA client id, so the ID token is what /ops/* expects. */
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}
